import c from 'classnames';
import React, { Fragment } from 'react';
import { Transition } from '@headlessui/react';

function ErrorMessage({ title, children, variant }) {
  return /*#__PURE__*/(
    React.createElement(React.Fragment, null,
    title ? /*#__PURE__*/
    React.createElement("h3", {
      className: c(variant !== 'success' ? 'text-red-800' : 'text-green-800', 'text-sm font-mediums') },

    title) :

    undefined, /*#__PURE__*/
    React.createElement("div", { className: c('text-sm', title ? 'mt-2' : '', variant === 'success' ? 'text-green-700' : 'text-red-700') },
    children)));



}

function HttpBadRequestErrorMessage({ error }) {
  const children = [];
  let k = Date.now();

  const processErrors = (items, prefix) => {
    if (Array.isArray(items)) {
      items.forEach((item) => {
        if (item.message) {
          item = item.message;
        } else {
          item = item.toString();
        }

        children.push( /*#__PURE__*/
        React.createElement("li", { key: k },
        prefix.length > 0 ? /*#__PURE__*/
        React.createElement("span", { className: "font-semibold" },
        prefix.join('.'),
        ': ') :

        undefined,
        item));



        k -= 1;
      });
    } else if (typeof items === 'object') {
      Object.entries(items).forEach(([key, values]) => {
        if (key === 'request') {
          processErrors(values, prefix);
        } else {
          processErrors(values, [...prefix, key]);
        }
      });
    } else {
      children.push( /*#__PURE__*/React.createElement("li", { key: k }, items.toString()));
      k -= 1;
    }
  };

  processErrors(error, []);
  return /*#__PURE__*/React.createElement("ul", { className: "list-disc pl-5 space-y-1" }, children);
}

function HttpErrorMessage({ title, error }) {
  const { response } = error;
  if (response.headers['content-type'].indexOf('application/json') >= 0) {
    if (response.status === 400) {
      title = 'Your submission has errors';
      error = /*#__PURE__*/React.createElement(HttpBadRequestErrorMessage, { error: response.data });
    } else {
      error = /*#__PURE__*/React.createElement(JSONViewer, { value: response.data });
    }
  } else {
    error = (error.message || error.statusText).toString();
  }

  return /*#__PURE__*/React.createElement(ErrorMessage, { title: title }, error);
}

function JSONViewer({ value }) {
  const formattedValue = JSON.stringify(value, null, 2);
  return /*#__PURE__*/React.createElement("pre", null, formattedValue);
}

const AlertBody = React.memo(({ title, error, variant }) => {
  if (typeof error === 'undefined' || error === null) {
    return /*#__PURE__*/React.createElement(React.Fragment, null);
  }

  if (typeof error === 'string') {
    return /*#__PURE__*/React.createElement(ErrorMessage, { title: title, variant: variant }, error);
  }

  if (error.response) {
    return /*#__PURE__*/React.createElement(HttpErrorMessage, { title: title, error: error });
  }

  if (error.message) {
    return /*#__PURE__*/React.createElement(ErrorMessage, { title: title }, error.message.toString());
  }

  return /*#__PURE__*/(
    React.createElement(ErrorMessage, { title: title }, /*#__PURE__*/
    React.createElement(JSONViewer, { value: error })));


});

AlertBody.displayName = 'AlertBody';

export default function ErrorAlert({
  title,
  error,
  className,
  variant,
  clearError,
  ...props })
{
  return /*#__PURE__*/(

    React.createElement(Transition, {
      show: !!error,
      as: Fragment,
      enter: "transform transition duration-[400ms]",
      enterFrom: "opacity-0 scale-95",
      enterTo: "opacity-100 scale-100",
      leave: "transform duration-200 transition ease-in-out",
      leaveFrom: "opacity-100 rotate-0 scale-100 ",
      leaveTo: "opacity-0 scale-95" }, /*#__PURE__*/

    React.createElement("div", Object.assign({ className: c(className, 'flex p-4 rounded', variant !== 'success' ? 'bg-red-50' : 'bg-green-50') }, props), /*#__PURE__*/
    React.createElement("div", { className: c(variant !== 'success' ? 'text-red-400' : 'text-green-400', 'flex-shrink-0') }, /*#__PURE__*/
    React.createElement("svg", { className: "h-5 w-5", fill: "currentColor", viewBox: "0 0 20 20" }, /*#__PURE__*/
    React.createElement("path", {
      fillRule: "evenodd",
      clipRule: "evenodd",
      d: "M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z" }))), /*#__PURE__*/



    React.createElement("div", { className: "ml-3 overflow-x-auto" }, /*#__PURE__*/
    React.createElement(AlertBody, { title: title, error: error, variant: variant })),

    typeof clearError === 'function' && /*#__PURE__*/
    React.createElement("div", { className: "ml-auto pl-3" }, /*#__PURE__*/
    React.createElement("div", { className: "-mx-1.5 -my-1.5" }, /*#__PURE__*/
    React.createElement("button", {
      type: "button",
      onClick: () => clearError(null),
      className: c(
      variant !== 'success' ?
      'text-red-500 hover:bg-red-100' : 'text-green-500 hover:bg-green-100',
      'inline-flex rounded-md p-1.5 text-red-500 hover:bg-red-100 focus:outline-none focus:bg-red-100 transition ease-in-out duration-150') }, /*#__PURE__*/



    React.createElement("svg", {
      className: "h-5 w-5",
      viewBox: "0 0 20 20",
      fill: "currentColor" }, /*#__PURE__*/

    React.createElement("path", {
      fillRule: "evenodd",
      clipRule: "evenodd",
      d: "M4.293 4.293a1 1 0 011.414 0L10 8.586l4.293-4.293a1 1 0 111.414 1.414L11.414 10l4.293 4.293a1 1 0 01-1.414 1.414L10 11.414l-4.293 4.293a1 1 0 01-1.414-1.414L8.586 10 4.293 5.707a1 1 0 010-1.414z" }))))))));











}